import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  UserCog, Loader2, Eye, EyeOff, ClipboardCheck, LogIn
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiderLoginPage = () => {
  const navigate = useNavigate();
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Formatar CPF enquanto digita
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 11) {
      setCpf(formatted);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cpfLimpo = cpf.replace(/\D/g, '');
    
    if (cpfLimpo.length !== 11) {
      toast.error('CPF inválido');
      return;
    }
    
    if (!senha) {
      toast.error('Digite sua senha');
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/lider/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cpf: cpfLimpo,
          senha: senha
        })
      });

      const data = await response.json();

      if (response.ok && data.access_token) {
        // Salvar token e dados do líder
        localStorage.setItem('lider_token', data.access_token);
        localStorage.setItem('lider_data', JSON.stringify(data.lider));
        
        toast.success(`Bem-vindo, ${data.lider.nome}!`);
        
        // Redirecionar para a tela de aprovações do unified
        navigate('/unified/aprovacoes');
      } else {
        toast.error(data.detail || 'CPF ou senha inválidos');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error('Erro ao conectar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl" data-testid="lider-login-card">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
            <UserCog className="w-10 h-10 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              Área do Líder
            </CardTitle>
            <p className="text-muted-foreground text-sm mt-1">
              Acesse para aprovar justificativas da sua equipe
            </p>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF</Label>
              <Input
                id="cpf"
                type="text"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={handleCPFChange}
                className="h-12 text-lg"
                autoComplete="username"
                data-testid="input-cpf-lider"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="senha">Senha</Label>
              <div className="relative">
                <Input
                  id="senha"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="h-12 text-lg pr-12"
                  autoComplete="current-password"
                  data-testid="input-senha-lider"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              disabled={loading}
              data-testid="btn-login-lider"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <LogIn className="w-5 h-5 mr-2" />
              )}
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <ClipboardCheck className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
              <p>
                Aqui você pode aprovar ou rejeitar justificativas de ponto 
                enviadas pelos empregados da sua equipe.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Logo/Branding */}
      <div className="fixed bottom-4 text-center w-full">
        <p className="text-white/60 text-sm">
          JORNYX - Gestão de Ponto
        </p>
      </div>
    </div>
  );
};

export default LiderLoginPage;
